import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, fmtINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Layers, 
  Users, 
  Search, 
  RefreshCw, 
  Edit2, 
  Check, 
  X, 
  Plus, 
  Save, 
  Building2,
  Filter,
  CheckCircle2
} from "lucide-react";
import { toast } from "sonner";

export default function PriceListMapping() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("groups"); // "groups" | "dealers"
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);

  // 1. Fetch Price Lists & Stats
  const { data: priceLists = [], isLoading: loadingLists, refetch: refetchLists } = useQuery({
    queryKey: ["price-lists-meta"],
    queryFn: async () => {
      const { data } = await api.get("/price-lists");
      return data || [];
    },
  });

  // 2. Fetch Dealer Price Mappings
  const { data: dealerMappings = [], isLoading: loadingDealers, refetch: refetchDealers } = useQuery({
    queryKey: ["dealer-price-mappings"],
    queryFn: async () => {
      const { data } = await api.get("/dealer-price-mappings");
      return data || [];
    },
  });

  // Rename Price List Mutation
  const renameMutation = useMutation({
    mutationFn: async ({ id, group_name }) => {
      const { data } = await api.put(`/price-lists/${id}`, { group_name });
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Renamed price list to "${data.group_name}"`);
      queryClient.invalidateQueries(["price-lists-meta"]);
      queryClient.invalidateQueries(["dealer-price-mappings"]);
      queryClient.invalidateQueries(["products"]);
      setEditingId(null);
      setEditingName("");
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Failed to rename price list");
    }
  });

  // Create Price List Mutation
  const createMutation = useMutation({
    mutationFn: async ({ group_name }) => {
      const { data } = await api.post("/price-lists", { group_name });
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Created price list group "${data.group_name}"`);
      queryClient.invalidateQueries(["price-lists-meta"]);
      setShowAddModal(false);
      setNewGroupName("");
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Failed to create price list");
    }
  });

  // Update Dealer Price Mapping Mutation
  const assignDealerMutation = useMutation({
    mutationFn: async ({ dealer_code, group_name }) => {
      const { data } = await api.post("/dealer-price-mappings", { dealer_code, group_name });
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Assigned ${data.dealer_code} to ${data.group_name}`);
      queryClient.invalidateQueries(["dealer-price-mappings"]);
      queryClient.invalidateQueries(["price-lists-meta"]);
      queryClient.invalidateQueries(["products"]);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Failed to update dealer price group");
    }
  });

  const handleStartRename = (pl) => {
    setEditingId(pl.id);
    setEditingName(pl.group_name);
  };

  const handleSaveRename = (id) => {
    if (!editingName.trim()) return toast.error("Group name cannot be empty");
    renameMutation.mutate({ id, group_name: editingName.trim() });
  };

  const handleCreateGroup = (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) return toast.error("Please enter a group name");
    createMutation.mutate({ group_name: newGroupName.trim() });
  };

  const filteredDealers = dealerMappings.filter(d => {
    if (groupFilter !== "all" && d.group_name !== groupFilter) return false;
    if (search) {
      const term = search.toLowerCase();
      const code = (d.dealer_code || "").toLowerCase();
      const name = (d.dealer_name || "").toLowerCase();
      const city = (d.city || "").toLowerCase();
      return code.includes(term) || name.includes(term) || city.includes(term);
    }
    return true;
  });

  return (
    <div className="space-y-6 pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Layers className="h-6 w-6 text-blue-600" />
            Price List & Group Mapping
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage SAP price lists, assign friendly naming tags, and link dealers to custom price tiers.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { refetchLists(); refetchDealers(); }}
            className="h-9 gap-1.5 text-xs text-slate-600"
          >
            <RefreshCw size={14} className={loadingLists || loadingDealers ? "animate-spin" : ""} />
            Refresh
          </Button>
          {user?.role === "admin" && (
            <Button
              size="sm"
              onClick={() => setShowAddModal(true)}
              className="h-9 gap-1.5 text-xs bg-slate-900 text-white hover:bg-slate-800"
            >
              <Plus size={14} />
              New Price List Group
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setActiveTab("groups")}
            className={`pb-3 text-sm font-semibold transition-all relative ${
              activeTab === "groups"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <div className="flex items-center gap-2">
              <Layers size={16} />
              <span>Price Groups & Tiers ({priceLists.length})</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab("dealers")}
            className={`pb-3 text-sm font-semibold transition-all relative ${
              activeTab === "dealers"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <div className="flex items-center gap-2">
              <Users size={16} />
              <span>Dealer Group Assignment ({dealerMappings.length})</span>
            </div>
          </button>
        </div>
      </div>

      {/* TAB 1: PRICE LIST GROUPS */}
      {activeTab === "groups" && (
        <div className="space-y-4">
          <Card className="border border-slate-200 shadow-xs overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-600 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 min-w-[200px]">Price Group Name</th>
                    <th className="text-left px-4 py-3 min-w-[120px]">Currency</th>
                    <th className="text-left px-4 py-3 min-w-[140px]">Priced Items</th>
                    <th className="text-left px-4 py-3 min-w-[140px]">Assigned Dealers</th>
                    <th className="text-right px-4 py-3 min-w-[140px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loadingLists ? (
                    <tr>
                      <td colSpan={5} className="text-center py-12 text-slate-500">Loading price groups...</td>
                    </tr>
                  ) : priceLists.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-12 text-slate-500">No price groups found.</td>
                    </tr>
                  ) : (
                    priceLists.map((pl) => {
                      const isEditing = editingId === pl.id;
                      return (
                        <tr key={pl.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3 font-medium text-slate-900">
                            {isEditing ? (
                              <div className="flex items-center gap-1.5 max-w-xs">
                                <Input
                                  value={editingName}
                                  onChange={(e) => setEditingName(e.target.value)}
                                  className="h-8 text-xs font-bold uppercase"
                                  autoFocus
                                />
                                <Button
                                  size="sm"
                                  onClick={() => handleSaveRename(pl.id)}
                                  className="h-8 w-8 p-0 bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                                >
                                  <Check size={14} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setEditingId(null)}
                                  className="h-8 w-8 p-0 text-slate-500 hover:bg-slate-100 shrink-0"
                                >
                                  <X size={14} />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-slate-900">{pl.group_name}</span>
                                {["admin", "accounts"].includes(user?.role) && (
                                  <button
                                    onClick={() => handleStartRename(pl)}
                                    className="text-slate-400 hover:text-blue-600 p-1 rounded transition-colors"
                                    title="Rename Group"
                                  >
                                    <Edit2 size={13} />
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-slate-600">
                            {pl.currency || "INR"}
                          </td>
                          <td className="px-4 py-3 text-xs font-semibold text-slate-800">
                            {pl.sku_count?.toLocaleString("en-IN") || 0} SKUs
                          </td>
                          <td className="px-4 py-3 text-xs font-semibold text-blue-600">
                            {pl.dealer_count || 0} Dealers
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setGroupFilter(pl.group_name);
                                setActiveTab("dealers");
                              }}
                              className="h-7 text-xs text-slate-600 hover:text-slate-900"
                            >
                              View Dealers
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* TAB 2: DEALER ASSIGNMENTS */}
      {activeTab === "dealers" && (
        <Card className="border border-slate-200 shadow-xs overflow-hidden">
          {/* Filter Bar */}
          <div className="p-4 bg-slate-50 border-b border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search dealer by name, code, or city..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9 text-xs bg-white"
              />
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <span className="text-xs text-slate-500 font-medium">Filter Group:</span>
              <select
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
                className="h-9 text-xs px-3 py-1 bg-white border border-slate-200 rounded-md text-slate-700 font-medium focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="all">All Groups ({dealerMappings.length})</option>
                {priceLists.map(pl => (
                  <option key={pl.id} value={pl.group_name}>{pl.group_name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto max-h-[calc(100vh-360px)] overflow-y-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 z-10 bg-slate-100 text-[10px] uppercase tracking-wider text-slate-600 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 min-w-[140px]">Dealer Code</th>
                  <th className="text-left px-4 py-3 min-w-[240px]">Dealer Name</th>
                  <th className="text-left px-4 py-3 min-w-[140px]">City / Area</th>
                  <th className="text-left px-4 py-3 min-w-[200px]">Assigned Price Group</th>
                  <th className="text-right px-4 py-3 min-w-[120px]">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loadingDealers ? (
                  <tr>
                    <td colSpan={5} className="text-center py-12 text-slate-500">Loading dealer mappings...</td>
                  </tr>
                ) : filteredDealers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-12 text-slate-500">No dealers match the search filter.</td>
                  </tr>
                ) : (
                  filteredDealers.map(d => (
                    <tr key={d.dealer_code} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-700">
                        {d.dealer_code}
                      </td>
                      <td className="px-4 py-3 text-xs font-medium text-slate-900">
                        {d.dealer_name}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {d.city || d.area || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={d.group_name}
                          onChange={(e) => assignDealerMutation.mutate({ dealer_code: d.dealer_code, group_name: e.target.value })}
                          disabled={!["admin", "accounts"].includes(user?.role)}
                          className="h-8 text-xs font-semibold px-2.5 py-1 bg-white border border-slate-200 rounded-md text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          {priceLists.map(pl => (
                            <option key={pl.id} value={pl.group_name}>{pl.group_name}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Badge variant="outline" className="text-[10px] text-emerald-700 bg-emerald-50 border-emerald-200 gap-1 inline-flex items-center">
                          <CheckCircle2 size={11} /> Active
                        </Badge>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* New Price List Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 border border-slate-200">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <h3 className="text-base font-bold text-slate-900">Create New Price List Group</h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={16} />
              </button>
            </div>
            <form onSubmit={handleCreateGroup} className="space-y-4 mt-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Group Label / Name (e.g. SOUTH RETAIL, MODERN TRADE)
                </label>
                <Input
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="e.g. MODERN TRADE"
                  className="h-9 text-xs uppercase"
                  autoFocus
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button type="button" variant="outline" size="sm" onClick={() => setShowAddModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={createMutation.isPending} className="bg-slate-900 text-white hover:bg-slate-800">
                  {createMutation.isPending ? "Creating..." : "Create Group"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
